import { useState, useEffect } from "react";
import { FaArrowLeft, FaCheck, FaShippingFast, FaHome, FaCreditCard, FaCalendarAlt, FaTruck, FaMapMarkerAlt, FaPhone, FaShoppingBag, FaBox } from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import "./OrderTracking.css";
import { Spinner } from "react-bootstrap"; 
import { apiFetch, getFullImageUrl, getProductById } from "../api"; 
import { getFallbackImage } from "../utils/imageLoader";
import { useDispatch } from "react-redux";
import { addToCart } from "../redux/cartSlice";
import ProductModal from "../components/ProductModal";

const OrderTracking = () => {
    // Initialize navigation and dispatch hooks
    const navigate = useNavigate();
    const dispatch = useDispatch();
    // Get orderId from URL params
    const { orderId } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ setImageLoadErrors] = useState({});
    const [isReordering, setIsReordering] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    // tracking steps aligned to shipment statuses
    const trackingSteps = [
        { 
            id: 1, 
            name: "Confirmed", 
            status: "confirmed", 
            icon: FaCheck,
            description: "Order confirmed"
        },
        { 
            id: 2, 
            name: "Packed", 
            status: "packed", 
            icon: FaBox,
            description: "Items packed"
        },
        { 
            id: 3, 
            name: "Shipped", 
            status: "shipped", 
            icon: FaShippingFast,
            description: "On the way"
        },
        { 
            id: 4, 
            name: "Out for Delivery", 
            status: "out-for-delivery", 
            icon: FaTruck,
            description: "Courier is nearby"
        },
        { 
            id: 5, 
            name: "Delivered", 
            status: "delivered", 
            icon: FaHome,
            description: "Delivery pending"
        }
    ];

    // Normalize and current status
    const normalizeStatus = (status) => String(status || '').toLowerCase();
    const resolveCurrentStatus = (status) => {
        const normalized = normalizeStatus(status);
        if (normalized === 'pending' || normalized === 'processing') return 'confirmed';
        if (normalized === 'completed') return 'delivered';
        return normalized;
    };

    // Get step status
    const getStepStatus = (currentStatus, stepStatus) => {
        const stepIndex = trackingSteps.findIndex(s => s.status === stepStatus);
        const currentIndex = trackingSteps.findIndex(s => s.status === currentStatus);
        if (currentIndex === -1) return stepIndex === 0 ? "active" : "";
        if (stepIndex < currentIndex) return "completed";
        if (stepIndex === currentIndex) return "active";
        return "";
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Format date time
    const formatDateTime = (dateString) => {
        if (!dateString) return "N/A";
        // Format date and time for display
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    //  Get expected delivery date
    const getExpectedDelivery = () => {
        if (order?.expectedDelivery) {
            return formatDate(order.expectedDelivery);
        }
        return "Will be updated soon";
    };

    // Get step date for tracking steps
    const getStepDate = (stepStatus) => {
        // Return date with the step status
        if (!order) return null;
        const normalizedStep = normalizeStatus(stepStatus);
        const statusDates = order.statusDates || order.shipmentDates || order.statusTimeline || order.statusHistory || {};
        const possibleKeys = [
            normalizedStep,
            normalizedStep.replace(/-/g, ' '),
            normalizedStep.replace(/-/g, ''),
            normalizedStep.replace(/-/g, '_')
        ];
        for (const key of possibleKeys) {
            if (statusDates[key]) {
                return formatDateTime(statusDates[key]);
            }
        }
        // Return createdAt for confirmed step if no specific date found
        if (normalizedStep === 'confirmed') {
            return order.createdAt ? formatDateTime(order.createdAt) : null;
        }

        // For other steps
        if (["packed", "shipped", "out-for-delivery", "delivered"].includes(normalizedStep)) {
            if (order.shipmentUpdatedAt) return formatDateTime(order.shipmentUpdatedAt);
            if (order.updatedAt) return formatDateTime(order.updatedAt);
        }
        
        return null;
    };

    //  Get shipment update date
    const getShipmentUpdatedAt = () => {
        if (order?.shipmentUpdatedAt) {
            return formatDateTime(order.shipmentUpdatedAt);
        }
        return null;
    };

    // Handle reorder
    const handleReorder = async () => {
        setIsReordering(true);
        try {
            const items = order.orderItems || [];
            if (items.length === 0) {
                toast.error('No items to reorder');
                return;
            }
            
            let successCount = 0;
            
            for (const item of items) {
                try {
                    // Fetch full product details
                    const productId = item.product?._id || item.product;
                    if (!productId) continue;
                    // full product details
                    const fullProduct = await getProductById(productId);
                    const product = fullProduct.data || fullProduct;
                    
                    if (!product) continue;
                    // Construct cart item
                    const cartItem = {
                        id: product._id,
                        title: product.name || product.title,
                        price: product.price || item.price,
                        // Use selected variants if available
                        image: getFullImageUrl(product.images?.[0]?.url || product.images?.[0] || product.image) || getFallbackImage(),
                        quantity: item.quantity || 1
                    };
                    // Add variant details if applicable
                    dispatch(addToCart(cartItem));
                    successCount++;
                } catch (err) {
                    console.error('Failed to add item:', err);
                }
            }
            // Notify user and navigate to cart
            if (successCount > 0) {
                window.dispatchEvent(new CustomEvent('cartNotification', {
                    detail: { 
                        type: 'cart', 
                        subType: 'add', 
                        message: `${successCount} item${successCount > 1 ? 's' : ''} added to cart`,
                        variantCount: successCount
                    }
                }));
                setTimeout(() => navigate('/cart'), 1500);
            }
        } catch (err) {
            toast.error('Failed to reorder: ' + err.message);
        } finally {
            setIsReordering(false);
        }
    };

    // Fetch order details
    useEffect(() => {
        const fetchOrderDetails = async () => {
            // Validate orderId
            if (!orderId) {
                toast.error("No order ID provided.");
                navigate('/orderhistory');
                return;
            }
            setLoading(true);
            setError(null);
            try {
                // Fetch order by ID
                const data = await apiFetch(`/api/orders/byId/${orderId}`);
                let fetchedOrder = data.order || data.data || data;

                // Enrich order items with full product details if missing
                if (Array.isArray(fetchedOrder.orderItems) && fetchedOrder.orderItems.length > 0) {
                    const enrichedItems = await Promise.all(fetchedOrder.orderItems.map(async (it) => {
                        // Enrich each order item with product details if missing
                        const prod = it.product || {};
                        const itemImage = it.imageUrl || it.image || null;
                        const productId = prod?._id || prod;

                        // Resolve variant details
                        const resolveVariant = (productData, variantId) => {
                            if (!productData || !productData.variants || !variantId) return {};
                            // Find variant by ID
                            const found = productData.variants.find(v => String(v._id) === String(variantId));
                            if (!found) return {};
                            return {
                                selectedColor: it.selectedColor || found.color || found.colour,
                                selectedSize: it.selectedSize || found.size
                            };
                        };
                        // Start with existing item data
                        let resolved = { ...it };
                        if (prod.variants) {
                            // Resolve variant info from product data
                            resolved = { ...resolved, ...resolveVariant(prod, it.variantId) };
                        }
                        // If product details already exist, return as is
                        if (prod.images || prod.image || prod.imageUrl || itemImage) return resolved;
                        if (!productId) return resolved;
                        // Fetch full product details
                        try {
                            // Fetch product by ID
                            const p = await getProductById(productId);
                            const productData = p?.data || p;
                            // Merge product data and variant info
                            resolved = { ...resolved, product: productData || prod, ...resolveVariant(productData, it.variantId) };
                            return resolved;
                        } catch (err) {
                            return resolved;
                        }
                    }));
                    fetchedOrder = { ...fetchedOrder, orderItems: enrichedItems };
                }
                setOrder(fetchedOrder);
            } catch (err) {
                setError(err.message);
                toast.error(`Failed to load order: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };
        
        fetchOrderDetails();
    }, [orderId, navigate]);

    // Loading state
    if (loading) {
        return (
            <div className="order-tracking-container">
                <div className="container py-5 text-center d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                    <div className="text-center">
                        <Spinner animation="border" className="loading-spinner" style={{ color: "#82287A", width: '3rem', height: '3rem' }} />
                        <div className="mt-4">
                            <h5 className="text-primary-purple mb-3">Loading Order Details</h5>
                            <p className="text-muted">Fetching your order information...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !order) {
        return (
            <div className="order-tracking-container">
                <div className="container py-5">
                    <div className="text-center">
                        <div className="alert alert-danger border-0 shadow-sm" style={{ maxWidth: '500px', margin: '0 auto' }}>
                            <h5 className="alert-heading">Order Not Found</h5>
                            <p className="mb-3">We couldn't find the order you're looking for.</p>
                            <Link to="/orderhistory" className="btn btn-primary-purple">
                                <FaArrowLeft className="me-2" />
                                Back to Order History
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Order data
    const currentStatus = resolveCurrentStatus(order.shipmentStatus || order.status || 'confirmed');
    
    // Handle address from multiple possible field structures
    const address = order.address || order.shippingAddress || {};
    const addressString = [
        address.street || address.addressLine1 || order.addressLine1,
        address.city || order.city,
        address.state || order.state,
        address.pincode || address.postal_code || order.pincode
    ].filter(Boolean).join(', ') || "Address not specified";
    
    const totalAmount = order.total_amount || 0;
    // const paymentStatus = order.payment?.status || order.paymentStatus || "Pending";
    const paymentMethod = order.payment?.method || order.paymentMethod || "COD";
    const courierName = order.courierName || "Standard Shipping";
    const expectedDelivery = getExpectedDelivery();
    
    // Calculate order summary details
    const orderItems = order.orderItems || [];
    const totalQty = orderItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const placedOn = formatDateTime(order.createdAt);
    const lastUpdated = formatDateTime(order.updatedAt);
    const statusLabel = (order.shipmentStatus || order.status || 'CONFIRMED').toUpperCase();

    // Get progress percentage
    const getProgressPercentage = () => {
        const currentIndex = trackingSteps.findIndex(step => step.status === currentStatus);
        if (currentIndex === -1) return (1 / trackingSteps.length) * 100;
        return ((currentIndex + 1) / trackingSteps.length) * 100;
    };
    // Handle product image click to show modal
    const handleProductImageClick = (product) => {
        setSelectedProduct(product);
        setShowModal(true);
    };

    return (
        <div className="order-tracking-container">
            <div className="container">
                {/* Header */}
                <div className="tracking-header">
                    <Link to="/orderhistory" className="back-button">
                        <FaArrowLeft />
                        Back to Orders
                    </Link>
                    <div>
                        <h1 className="h3 mb-0">Order Tracking</h1>
                        <p className="text-muted mb-0 small">Track your order in real-time</p>
                    </div>
                </div>

                {/* Main Card */}
                <div className="tracking-card">
                    {/* Order Header */}
                    <div className="order-header">
                        <div className="order-id-section">
                            <div>
                                <p className="order-id-label">TRACKING ID</p>
                                <h2 className="order-id-value">{order.trackingId || 'Not assigned yet'}</h2>
                            </div>
                            <div className="d-flex flex-column align-items-end gap-2">
                                <span className="status-badge-new">
                                    {statusLabel}
                                </span>
                                <div className="order-placed">
                                    <FaCalendarAlt />
                                    <span>Placed on {placedOn}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Progress Section */}
                    <div className="progress-section">
                        <div className="progress-header">
                            <h3 className="progress-title">Delivery Progress</h3>
                            <div className="estimated-delivery">
                                <FaCalendarAlt />
                                <span>Estimated Delivery: <strong>{expectedDelivery}</strong></span>
                            </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="progress-container">
                            <div 
                                className="progress-fill"
                                style={{ width: `${getProgressPercentage()}%` }}
                            />
                        </div>

                        {/* Steps */}
                        <div className="steps-container">
                            {trackingSteps.map((step) => {
                                const status = getStepStatus(currentStatus, step.status);
                                const isCompleted = status === "completed" || status === "active";
                                const showDate = status === "completed" || status === "active";
                                const stepDate = showDate ? getStepDate(step.status) : null;
                                
                                return (
                                    <div 
                                        key={step.id} 
                                        className={`step-item ${status}`}
                                    >
                                        <div className="step-icon">
                                            {isCompleted ? <FaCheck /> : <step.icon />}
                                        </div>
                                        <span className="step-label">{step.name}</span>
                                        <span className="step-date">
                                            {stepDate ? stepDate : (isCompleted ? "Completed" : step.description)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Order Summary */}
                    <div className="order-summary">
                        <div className="product-preview">
                            <div className="product-image-container" style={{ cursor: 'pointer' }} onClick={() => orderItems[0]?.product && handleProductImageClick(orderItems[0].product)}>
                                {orderItems[0]?.product?.images?.[0] ? (
                                    <img 
                                        src={getFullImageUrl(orderItems[0].product.images[0].url || orderItems[0].product.images[0])}
                                        alt={orderItems[0].product.name}
                                        className="product-image"
                                        onError={(e) => {
                                            e.target.src = getFallbackImage();
                                        }}
                                    />
                                ) : (
                                    <div className="product-image-placeholder">
                                        <FaShoppingBag size={48} color="#82287A" />
                                    </div>
                                )}
                            </div>
                            <div className="product-info">
                                <h3 className="product-title" style={{ cursor: 'pointer' }} onClick={() => orderItems[0]?.product && handleProductImageClick(orderItems[0].product)}>
                                    {orderItems[0]?.product?.name || "Your Order"}
                                </h3>
                                <div className="product-meta">
                                    <span>{totalQty} item{totalQty !== 1 ? 's' : ''}</span>
                                    <span>Order Total: ₹{totalAmount.toFixed(2)}</span>
                                </div>
                                <div className="product-price">
                                    ₹{totalAmount.toFixed(2)}
                                </div>
                                <p className="text-muted mt-2 small">
                                    Last updated: {lastUpdated}
                                </p>
                            </div>
                        </div>
                        
                        <div className="order-actions">
                            <button 
                                className="action-button primary"
                                onClick={handleReorder}
                                disabled={isReordering}
                            >
                                {isReordering ? (
                                    <>
                                        <Spinner size="sm" />
                                        Adding to Cart...
                                    </>
                                ) : (
                                    <>
                                        <FaShoppingBag />
                                        Reorder All Items
                                    </>
                                )}
                            </button>
                            <button 
                                className="action-button outline"
                                onClick={() => navigate('/contact')}
                            >
                                <FaPhone />
                                Contact Support
                            </button>
                        </div>
                    </div>

                    {/* Details Grid */}
                    <div className="details-grid">
                        <div className="detail-card">
                            <div className="detail-header">
                                <FaMapMarkerAlt className="detail-icon" />
                                <h4 className="detail-title">Shipping Address</h4>
                            </div>
                            <div className="detail-content">
                                <p className="mb-2">{addressString}</p>
                                <div className="mt-3">
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <FaTruck size={14} />
                                        <span className="small">{courierName}</span>
                                    </div>
                                    
                                    {/* Shipment Status Badge */}
                                    {order.shipmentStatus && (
                                        <div className="mb-2">
                                            <span className="badge" style={{
                                                backgroundColor: order.shipmentStatus === 'delivered' ? '#28a745' : 
                                                               order.shipmentStatus === 'out-for-delivery' ? '#ffc107' : 
                                                               order.shipmentStatus === 'shipped' ? '#17a2b8' : 
                                                               order.shipmentStatus === 'packed' ? '#6c757d' : '#6c757d',
                                                color: 'white',
                                                fontSize: '11px',
                                                fontWeight: '600',
                                                padding: '4px 10px',
                                                borderRadius: '12px',
                                                textTransform: 'capitalize'
                                            }}>
                                                {order.shipmentStatus.replace('-', ' ')}
                                            </span>
                                        </div>
                                    )}
                                    
                                    {/* Tracking ID Display */}
                                    {order.trackingId && (
                                        <div className="tracking-id-container mb-3" style={{
                                            backgroundColor: '#f8f9fa',
                                            border: '2px solid #82278A',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            marginTop: '12px'
                                        }}>
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div>
                                                    <p className="mb-1" style={{ 
                                                        fontSize: '12px', 
                                                        color: '#666',
                                                        fontWeight: '600',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}>
                                                        Tracking ID
                                                    </p>
                                                    <p className="mb-0" style={{ 
                                                        fontSize: '16px', 
                                                        fontWeight: '700',
                                                        color: '#82278A',
                                                        letterSpacing: '0.5px',
                                                        fontFamily: 'monospace'
                                                    }}>
                                                        {order.trackingId}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(order.trackingId);
                                                        toast.success('Tracking ID copied to clipboard!');
                                                    }}
                                                    style={{
                                                        padding: '6px 12px',
                                                        backgroundColor: '#82278A',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseOver={(e) => e.target.style.backgroundColor = '#6a1f66'}
                                                    onMouseOut={(e) => e.target.style.backgroundColor = '#82278A'}
                                                >
                                                    Copy
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {expectedDelivery !== "Will be updated soon" ? (
                                        <div className="badge bg-success text-white">
                                            📦 Expected: {expectedDelivery}
                                        </div>
                                    ) : (
                                        <div className="badge bg-warning text-dark">
                                            ⏳ {expectedDelivery}
                                        </div>
                                    )}
                                    {getShipmentUpdatedAt() && (
                                        <p className="text-muted small mt-2 mb-0">
                                            Last updated: {getShipmentUpdatedAt()}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="detail-card">
                            <div className="detail-header">
                                <FaCreditCard className="detail-icon" />
                                <h4 className="detail-title">Payment Information</h4>
                            </div>
                            <div className="detail-content">
                                <div className="mb-3">
                                    <p className="mb-1">Total Amount</p>
                                    {order.couponDiscount && order.couponDiscount > 0 ? (
                                        <div>
                                            <div style={{ textDecoration: 'line-through', color: '#dc3545', fontSize: '16px', marginBottom: '4px' }}>
                                                ₹{((totalAmount || 0) + order.couponDiscount).toFixed(2)}
                                            </div>
                                            <h3 className="mb-0" style={{ color: '#28a745' }}>₹{totalAmount.toFixed(2)}</h3>
                                            <div style={{ 
                                                display: 'inline-block',
                                                marginTop: '8px',
                                                padding: '4px 12px',
                                                backgroundColor: '#d4edda',
                                                border: '1px solid #c3e6cb',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                color: '#155724',
                                                fontWeight: '600'
                                            }}>
                                                🎉 You saved ₹{order.couponDiscount.toFixed(2)} with coupons!
                                            </div>
                                        </div>
                                    ) : (
                                        <h3 className="text-primary-purple">₹{totalAmount.toFixed(2)}</h3>
                                    )}
                                </div>
                                <div className="mb-3">
                                    <p className="mb-1">Payment Method</p>
                                    <p className="fw-semibold">{paymentMethod}</p>
                                </div>
                                {/* <div>
                                    <p className="mb-1">Payment Status</p>
                                    <span className={`badge bg-${getStatusColor(paymentStatus)}`}>
                                        {paymentStatus.toUpperCase()}
                                    </span>
                                </div> */}
                            </div>
                        </div>
                    </div>

                    {/* Order Items */}
                    <div className="items-section">
                        <h4 className="section-title">Order Items ({orderItems.length})</h4>
                        <div className="items-grid">
                            {orderItems.map((item, index) => {
                                const product = item.product || {};
                                const itemId = item._id || `item-${index}`;
                                const imageSrc = getFullImageUrl(
                                    product.images?.[0]?.url ||
                                    product.images?.[0] ||
                                    product.imageUrl ||
                                    product.image ||
                                    item.imageUrl ||
                                    item.image
                                ) || getFallbackImage();

                                return (
                                    <div key={itemId} className="item-card">
                                        <div className="item-header" style={{ cursor: 'pointer' }} onClick={() => product._id && handleProductImageClick(product)}>
                                            <img 
                                                src={imageSrc}
                                                alt={product.name || "Product"}
                                                className="item-image"
                                                onError={(e) => {
                                                    e.target.src = getFallbackImage();
                                                    setImageLoadErrors(prev => ({...prev, [itemId]: true}));
                                                }}
                                            />
                                            <div className="item-info">
                                                <h5 className="item-name" style={{ cursor: 'pointer' }} onClick={() => product._id && handleProductImageClick(product)}>
                                                    {product.name || `Item ${index + 1}`}
                                                </h5>
                                                {(item.color || item.selectedColor || item.size || item.selectedSize) && (
                                                    <div className="item-variants" style={{ display: 'flex', gap: '8px', marginTop: '4px', marginBottom: '4px' }}>
                                                        {(item.color || item.selectedColor) && (
                                                            <span style={{
                                                                backgroundColor: '#f0e6f5',
                                                                color: '#82278A',
                                                                padding: '2px 8px',
                                                                borderRadius: '4px',
                                                                fontSize: '0.85rem',
                                                                fontWeight: '500'
                                                            }}>
                                                                {item.color || item.selectedColor}
                                                            </span>
                                                        )}
                                                        {(item.size || item.selectedSize) && (
                                                            <span style={{
                                                                backgroundColor: '#f0e6f5',
                                                                color: '#82278A',
                                                                padding: '2px 8px',
                                                                borderRadius: '4px',
                                                                fontSize: '0.85rem',
                                                                fontWeight: '500'
                                                            }}>
                                                                Size: {item.size || item.selectedSize}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                                <p className="item-price">
                                                    ₹{(item.price || 0).toFixed(2)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="item-footer">
                                            <span className="quantity-badge">
                                                Quantity: {item.quantity || 1}
                                            </span>
                                            <span className="item-total">
                                                ₹{((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Additional Help Section */}
                <div className="text-center mt-4 mb-5">
                    <p className="text-muted small">
                        Need help with your order?{" "}
                        <Link to="/contact" className="text-primary-purple text-decoration-none">
                            Contact our support team
                        </Link>
                    </p>
                </div>
            </div>

            {/* Product Modal */}
            {selectedProduct && (
                <ProductModal
                    post={selectedProduct}
                    isOpen={showModal}
                    onClose={() => {
                        setShowModal(false);
                        setSelectedProduct(null);
                    }}
                />
            )}
        </div>
    );
};

export default OrderTracking;